import React, { useMemo } from "react";
import { connect, ConnectedProps } from "react-redux";
import { Dispatch } from "redux";
import reportSelectors from "redux/app/report/selectors";
import { transformAllVesselsGarbageDischargeDataForTable } from "redux/app/report/utils";
import { IStore } from "redux/store";

const garbageDischargeAllVesselsHeaders = [
	"Year",
	"Quarter",
	"Incinerated",
	"Discharged at sea",
	"Disposed ashore",
	"Accidental release",
	"Total garbage",
];

type OwnProps = {};
type GarbageDischargeTableProps = PropsFromRedux & OwnProps;
function GarbageDischargeTable(props: GarbageDischargeTableProps) {
	const { garbageDischargeAllVesselsData, year } = props;
	const tableRowRecords = useMemo(() => {
		return Object.keys(garbageDischargeAllVesselsData).length
			? transformAllVesselsGarbageDischargeDataForTable(
					garbageDischargeAllVesselsData
			  )
			: [];
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [garbageDischargeAllVesselsData]);

	const handleExportCSV = () => {
		if (!tableRowRecords || !tableRowRecords.length) return;

		// Generate CSV content
		const rows = tableRowRecords.map((row) => {
			const values = [
				year,
				row.quarter,
				row.incinerated.toFixed(2),
				row.dischargedToSea.toFixed(2),
				row.disposedAshore.toFixed(2),
				row.accidentalRelease.toFixed(2),
				row.quarterTotal.toFixed(2),
			];

			// Handle special characters and format values
			return values
				.map((val) => {
					if (val === null || val === undefined) return "";
					const str = String(val);
					// Escape quotes and wrap in quotes if contains comma or newline
					const escaped = str.replace(/"/g, '""');
					return /[,\n"]/.test(escaped) ? `"${escaped}"` : escaped;
				})
				.join(",");
		});

		// Combine headers and rows
		const csvContent = [
			garbageDischargeAllVesselsHeaders.join(","),
			...rows,
		].join("\n");

		// Create and trigger download
		const blob = new Blob([csvContent], { type: "text/csv;charset=utf-8;" });
		const link = document.createElement("a");
		const url = URL.createObjectURL(blob);

		// Set filename with current date for better tracking
		const date = new Date().toISOString().split("T")[0];
		const filename = `garbage_discharge_${date}_${year}.csv`;

		link.setAttribute("href", url);
		link.setAttribute("download", filename);
		document.body.appendChild(link);
		link.click();
		document.body.removeChild(link);
		URL.revokeObjectURL(url);
	};
	return (
		<div>
			<div className="flex justify-end mb-4">
				<button
					onClick={handleExportCSV}
					className="p-3 px-6 py-1 mt-1 mr-1 text-base text-indigo-700 border border-indigo-600 rounded-sm outline-none ws-button-og hover:bg-indigo-50 focus:ring-2 focus:ring-indigo-500 focus:ring-offset-2 transition-colors"
					disabled={!tableRowRecords || tableRowRecords.length === 0}
					title={
						!tableRowRecords || tableRowRecords.length === 0
							? "No data to export"
							: "Export data as CSV"
					}
				>
					<span className="mr-3 text-sm">Export Data</span>
					<span className="mb-0.5 text-sm bp3-icon bp3-icon-download"></span>
				</button>
			</div>
			<h2 className="text-xl font-medium mb-3">
				Garbage by disposal method (m³)
			</h2>{" "}
			<table className="w-full h-full border border-gray-400 table-auto rounded-md font-redhat-text  overflow-auto  border-collapse text-center">
				<thead>
					<tr>
						{garbageDischargeAllVesselsHeaders.map((headerLabel) => (
							<th
								key={headerLabel}
								className="px-3 py-3 text-sm border font-medium leading-4 tracking-wide text-center text-gray-700 bg-gray-100 border-b border-gray-300"
							>
								{headerLabel}
							</th>
						))}
					</tr>
				</thead>
				<tbody>
					{tableRowRecords.map((row, index) => {
						const {
							quarter,
							disposedAshore,
							dischargedToSea,
							incinerated,
							accidentalRelease,
							quarterTotal,
						} = row;
						return (
							<tr key={row.quarter}>
								{index === 0 && (
									<td
										rowSpan={5}
										className="px-1 text-center py-3 text-[8px] lg:text-xs xl:text-[12px] border leading-4 tracking-wide border-b border-gray-300 font-redhat-text text-[#2D2D2D]"
									>
										{year}
									</td>
								)}
								<td className="px-1 text-center py-3 text-[8px] lg:text-xs xl:text-[12px] border leading-4 tracking-wide border-b border-gray-300 font-redhat-text text-[#2D2D2D]">
									{quarter}
								</td>
								<td className="px-1 text-center py-3 text-[8px] lg:text-xs xl:text-[12px] border leading-4 tracking-wide border-b border-gray-300 font-redhat-text text-[#2D2D2D]">
									{incinerated.toFixed(2)}
								</td>
								<td className="px-1 text-center py-3 text-[8px] lg:text-xs xl:text-[12px] border leading-4 tracking-wide border-b border-gray-300 font-redhat-text text-[#2D2D2D]">
									{dischargedToSea.toFixed(2)}
								</td>
								<td className="px-1 text-center py-3 text-[8px] lg:text-xs xl:text-[12px] border leading-4 tracking-wide border-b border-gray-300 font-redhat-text text-[#2D2D2D]">
									{disposedAshore.toFixed(2)}
								</td>
								<td className="px-1 text-center py-3 text-[8px] lg:text-xs xl:text-[12px] border leading-4 tracking-wide border-b border-gray-300 font-redhat-text text-[#2D2D2D]">
									{accidentalRelease.toFixed(2)}
								</td>
								<td className="px-1 text-center py-3 text-[8px] lg:text-xs xl:text-[12px] border leading-4 tracking-wide border-b border-gray-300 font-redhat-text text-[#2D2D2D]">
									{quarterTotal.toFixed(2)}
								</td>
							</tr>
						);
					})}
				</tbody>
			</table>
		</div>
	);
}

function mapStateToProps(store: IStore, ownProps: OwnProps) {
	const reportStore = reportSelectors._getStore(store);
	const garbageDischargeAllVesselsData =
		reportSelectors.getGarbageDischargeAllVesselsData(reportStore);
	const year = reportSelectors.getGarbageYear(reportStore);

	return {
		garbageDischargeAllVesselsData,
		year,
	};
}
function mapDispatchToProps(dispatch: Dispatch, ownProps: OwnProps) {
	return {};
}
const reduxConnector = connect(mapStateToProps, mapDispatchToProps);
type PropsFromRedux = ConnectedProps<typeof reduxConnector>;
export default reduxConnector(GarbageDischargeTable);
